import { createContext, useContext, useState, useEffect, useMemo } from 'react';

const GuidanceContext = createContext(null);

/**
 * User Guidance System
 * Provides subtle golden glow on UI elements to guide users through the workflow:
 * 1. Dashboard → Glow on "Create" button
 * 2. After creating content → Glow on "Posts" menu + "Review & Approve" tab
 * 3. After approving → Glow on "Scheduled" tab + "Calendar"
 * 
 * The glow is always-on (not limited to first N uses) but subtle enough to not be intrusive.
 */
export function GuidanceProvider({ children, currentPath = '/' }) {
  const [guidanceState, setGuidanceState] = useState({
    // Track what the user has done in this session
    hasCreatedContent: false,
    hasVisitedPosts: false,
    hasApprovedPost: false,
    hasViewedScheduled: false,
    hasViewedCalendar: false,
  });

  // Determine what should glow based on current state and location
  const glowTargets = useMemo(() => {
    const targets = {
      create: false,
      posts: false,
      reviewApprove: false,
      scheduled: false,
      calendar: false,
    };

    // If on dashboard or home, glow Create
    if (currentPath === '/dashboard' || currentPath === '/') {
      targets.create = true;
    }

    // If user just created content (redirected to posts), glow Posts + Review
    if (guidanceState.hasCreatedContent && !guidanceState.hasApprovedPost) {
      targets.posts = true;
      targets.reviewApprove = true;
    }

    // If on posts page and has approved, glow Scheduled + Calendar
    if (guidanceState.hasApprovedPost && !guidanceState.hasViewedScheduled) {
      targets.scheduled = true;
      targets.calendar = true;
    }

    return targets;
  }, [currentPath, guidanceState]);

  // Track page visits
  useEffect(() => {
    if (currentPath === '/posts') {
      setGuidanceState(prev => ({ ...prev, hasVisitedPosts: true }));
    }
    if (currentPath === '/calendar') {
      setGuidanceState(prev => ({ ...prev, hasViewedCalendar: true, hasViewedScheduled: true }));
    }
  }, [currentPath]);

  // Methods to update guidance state
  const markContentCreated = () => {
    setGuidanceState(prev => ({ ...prev, hasCreatedContent: true }));
  };

  const markPostApproved = () => {
    setGuidanceState(prev => ({ ...prev, hasApprovedPost: true }));
  };

  const markScheduledViewed = () => {
    setGuidanceState(prev => ({ ...prev, hasViewedScheduled: true }));
  };

  const resetGuidance = () => {
    setGuidanceState({
      hasCreatedContent: false,
      hasVisitedPosts: false,
      hasApprovedPost: false,
      hasViewedScheduled: false,
      hasViewedCalendar: false,
    });
  };

  return (
    <GuidanceContext.Provider value={{
      glowTargets,
      guidanceState,
      markContentCreated,
      markPostApproved,
      markScheduledViewed,
      resetGuidance,
    }}>
      {children}
    </GuidanceContext.Provider>
  );
}

export function useGuidance() {
  const context = useContext(GuidanceContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      glowTargets: { create: false, posts: false, reviewApprove: false, scheduled: false, calendar: false },
      guidanceState: {},
      markContentCreated: () => {},
      markPostApproved: () => {},
      markScheduledViewed: () => {},
      resetGuidance: () => {},
    };
  }
  return context;
}
